import React, { useState, useEffect, useRef } from 'react';
import './BugReportModal.css';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';

const BugReportModal = ({ gameState, recentActions, currentBattle, onClose }) => {
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    // Auto-focus the textarea on open
    if (textareaRef.current) textareaRef.current.focus();
  }, []);

  const handleSubmit = async () => {
    if (!description.trim()) {
      setError('Please describe the bug you encountered.');
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');

      // Capture a snapshot of the game state
      const stateSnapshot = {
        character: gameState.character ? {
          ...gameState.character,
          // Ensure we have the key fields
          name: gameState.character.name,
          class: gameState.character.class,
          race: gameState.character.race,
          level: gameState.character.level,
          max_hp: gameState.character.max_hp,
          damage: gameState.character.damage,
          max_mp: gameState.character.max_mp,
          used_mp: gameState.character.used_mp,
          current_area: gameState.character.current_area,
          x_position: gameState.character.x_position,
          y_position: gameState.character.y_position,
          battle_state: gameState.character.battle_state,
          equipment: gameState.character.equipment,
          inventory: gameState.character.inventory,
          gold: gameState.character.gold,
          experience: gameState.character.experience,
          active_buffs: gameState.character.active_buffs,
        } : null,
        area: gameState.area,
        isConnected: gameState.isConnected,
        nearbyPlayers: gameState.nearbyPlayers?.length || 0,
      };

      // Include current combat state if in a battle
      const combatSnapshot = currentBattle ? {
        status: currentBattle.status,
        round: currentBattle.round,
        turn: currentBattle.turn,
        monster: currentBattle.monster ? {
          name: currentBattle.monster.name,
          level: currentBattle.monster.level,
          hp: currentBattle.monster.hp,
          currentHp: currentBattle.monster.currentHp,
          buffs: currentBattle.monster.buffs?.length || 0,
          debuffs: currentBattle.monster.debuffs?.length || 0,
        } : null,
        character: currentBattle.character ? {
          currentHp: currentBattle.character.currentHp,
          hp: currentBattle.character.hp,
          currentMp: currentBattle.character.currentMp,
          buffs: currentBattle.character.buffs?.map(b => `${b.name}(${b.rounds}r)`) || [],
          debuffs: currentBattle.character.debuffs?.map(d => `${d.name || d.type}(${d.rounds}r)`) || [],
        } : null,
        deck: currentBattle.deck ? {
          ap: currentBattle.deck.ap,
          maxAp: currentBattle.deck.maxAp,
          block: currentBattle.deck.block,
          handSize: currentBattle.deck.hand?.length || 0,
          hand: currentBattle.deck.hand?.map(c => `${c.name}(${c.apCost}AP)`) || [],
          drawPileCount: currentBattle.deck.drawPileCount,
          discardCount: currentBattle.deck.discardPile?.length || 0,
          exhaustCount: currentBattle.deck.exhaustPile?.length || 0,
        } : null,
        logTail: (currentBattle.log || []).slice(-10),
      } : null;

      const response = await fetch(`${API_BASE_URL}/api/bugs/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reportType: 'manual',
          description: description.trim(),
          gameState: stateSnapshot,
          combatState: combatSnapshot,
          recentActions: recentActions || [],
          clientUrl: window.location.href,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to submit report');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Failed to submit bug report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
    // Ctrl+Enter / Cmd+Enter to submit
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && !submitting && !submitted) {
      handleSubmit();
    }
  };

  return (
    <div className="bugreport-overlay" onClick={onClose} onKeyDown={handleKeyDown}>
      <div className="bugreport-modal" onClick={e => e.stopPropagation()}>
        <div className="bugreport-header">
          <h2>🐛 Report a Bug</h2>
          <button className="bugreport-close" onClick={onClose}>✕</button>
        </div>

        {submitted ? (
          <div className="bugreport-body">
            <div className="bugreport-success">
              <span className="bugreport-success-icon">✅</span>
              <h3>Bug Report Submitted!</h3>
              <p>Thank you for helping improve Realms of Loria. Your report has been sent to the development team.</p>
              <button className="bugreport-btn bugreport-btn-close" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        ) : (
          <div className="bugreport-body">
            <p className="bugreport-hint">
              Describe the bug you encountered. Your current game state and recent actions will be automatically included.
            </p>
            <textarea
              ref={textareaRef}
              className="bugreport-textarea"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What happened? What did you expect to happen? Any steps to reproduce?"
              maxLength={5000}
              rows={8}
              onKeyDown={handleKeyDown}
            />
            <div className="bugreport-char-count">
              {description.length}/5000
            </div>

            {error && <div className="bugreport-error">{error}</div>}

            <div className="bugreport-footer">
              <div className="bugreport-auto-info">
                📎 Game state + last {recentActions?.length || 0} actions will be attached
              </div>
              <div className="bugreport-buttons">
                <button className="bugreport-btn bugreport-btn-cancel" onClick={onClose}>
                  Cancel
                </button>
                <button
                  className="bugreport-btn bugreport-btn-submit"
                  onClick={handleSubmit}
                  disabled={submitting || !description.trim()}
                >
                  {submitting ? '⏳ Sending...' : '📤 Submit Report'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BugReportModal;
